import { createContext, useContext, useEffect, useState, useRef } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isAdmin: boolean;
  isViewer: boolean;
  isBlogEditor: boolean;
  isBibleSchoolEditor: boolean;
  isAnnouncementEditor: boolean;
  isSermonEditor: boolean;
  isMusicEditor: boolean;
  isMusicViewer: boolean;
  isMember: boolean;
  mfaRequired: boolean;
  pendingMFAUserId: string | null;
  signIn: (email: string, password: string) => Promise<{ error: any; mfaRequired?: boolean }>;
  signUp: (email: string, password: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  completeMFAVerification: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// MFA timeout: 5 minutes
const MFA_TIMEOUT_MS = 5 * 60 * 1000;

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isViewer, setIsViewer] = useState(false);
  const [isBlogEditor, setIsBlogEditor] = useState(false);
  const [isBibleSchoolEditor, setIsBibleSchoolEditor] = useState(false);
  const [isAnnouncementEditor, setIsAnnouncementEditor] = useState(false);
  const [isSermonEditor, setIsSermonEditor] = useState(false);
  const [isMusicEditor, setIsMusicEditor] = useState(false);
  const [isMusicViewer, setIsMusicViewer] = useState(false);
  const [isMember, setIsMember] = useState(false);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [pendingMFAUserId, setPendingMFAUserId] = useState<string | null>(null);
  
  // Use ref to track MFA state without blocking auth updates
  const mfaTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isProcessingMFARef = useRef(false);

  useEffect(() => {
    let rolesChannel: ReturnType<typeof supabase.channel> | null = null;

    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Skip updates if we're actively processing MFA
        if (isProcessingMFARef.current) {
          return;
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // Check user roles when auth state changes
        if (session?.user) {
          setTimeout(() => {
            checkUserRoles(session.user.id);
          }, 0);
        } else {
          clearAllRoles();
        }
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        checkUserRoles(session.user.id).finally(() => setLoading(false));
        
        // Set up realtime listener for role changes
        rolesChannel = supabase
          .channel('user-roles-changes')
          .on(
            'postgres_changes',
            {
              event: '*',
              schema: 'public',
              table: 'user_roles',
              filter: `user_id=eq.${session.user.id}`,
            },
            () => {
              // Refresh roles when they change
              checkUserRoles(session.user.id);
            }
          )
          .subscribe();
      } else {
        setLoading(false);
      }
    });

    return () => {
      subscription.unsubscribe();
      if (rolesChannel) {
        rolesChannel.unsubscribe();
      }
      if (mfaTimeoutRef.current) {
        clearTimeout(mfaTimeoutRef.current);
      }
    };
  }, []);

  const clearAllRoles = () => {
    setIsAdmin(false);
    setIsViewer(false);
    setIsBlogEditor(false);
    setIsBibleSchoolEditor(false);
    setIsAnnouncementEditor(false);
    setIsSermonEditor(false);
    setIsMusicEditor(false);
    setIsMusicViewer(false);
    setIsMember(false);
  };

  const checkUserRoles = async (userId: string) => {
    try {
      // Use secure get_my_roles() function instead of direct table access
      const { data, error } = await supabase.rpc('get_my_roles');

      if (!error && data) {
        const roles = data.map(r => r.role);
        setIsAdmin(roles.includes('admin'));
        setIsViewer(roles.includes('viewer'));
        setIsBlogEditor(roles.includes('blog_editor'));
        setIsBibleSchoolEditor(roles.includes('bible_school_editor'));
        setIsAnnouncementEditor(roles.includes('announcement_editor'));
        setIsSermonEditor(roles.includes('sermon_editor'));
        setIsMusicEditor(roles.includes('music_editor'));
        setIsMusicViewer(roles.includes('music_viewer'));
        setIsMember(roles.includes('member'));
      } else {
        clearAllRoles();
      }
    } catch (error) {
      console.error('[INTERNAL] Error checking user roles:', error);
      clearAllRoles();
    }
  };

  const startMFATimeout = () => {
    // Clear any existing timeout
    if (mfaTimeoutRef.current) {
      clearTimeout(mfaTimeoutRef.current);
    }
    
    // Set new timeout
    mfaTimeoutRef.current = setTimeout(async () => {
      // Auto-logout if MFA not completed
      await supabase.auth.signOut();
      setMfaRequired(false);
      setPendingMFAUserId(null);
      isProcessingMFARef.current = false;
    }, MFA_TIMEOUT_MS);
  };

  const signIn = async (email: string, password: string) => {
    isProcessingMFARef.current = true;
    
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    if (error) {
      isProcessingMFARef.current = false;
      // Log failed login
      await supabase.rpc('log_auth_event', {
        _user_id: data?.user?.id || null,
        _event_type: 'failed_login',
        _metadata: { error: error.message }
      });
      return { error };
    }
    
    // Check if MFA is enabled for this user
    if (data.user) {
      const { data: mfaSettings, error: mfaError } = await supabase
        .from('user_mfa_settings')
        .select('mfa_enabled')
        .eq('user_id', data.user.id)
        .eq('mfa_enabled', true)
        .maybeSingle();
      
      if (mfaSettings) {
        // MFA is required - show MFA prompt
        setMfaRequired(true);
        setPendingMFAUserId(data.user.id);
        startMFATimeout();
        
        // Log that MFA is required
        await supabase.rpc('log_auth_event', {
          _user_id: data.user.id,
          _event_type: 'mfa_required',
          _metadata: null
        });
        
        return { error: null, mfaRequired: true };
      }
      
      // No MFA required - complete sign in
      isProcessingMFARef.current = false;
      setSession(data.session);
      setUser(data.user);
      checkUserRoles(data.user.id);
      
      // Log successful login
      await supabase.rpc('log_auth_event', {
        _user_id: data.user.id,
        _event_type: 'successful_login',
        _metadata: null
      });
    }
    
    return { error };
  };

  const completeMFAVerification = async () => {
    // Clear timeout
    if (mfaTimeoutRef.current) {
      clearTimeout(mfaTimeoutRef.current);
    }
    
    // Allow auth state updates
    isProcessingMFARef.current = false;
    
    const { data } = await supabase.auth.getSession();
    if (data.session) {
      setSession(data.session);
      setUser(data.session.user);
      checkUserRoles(data.session.user.id);
      
      // Log successful login after MFA
      await supabase.rpc('log_auth_event', {
        _user_id: data.session.user.id,
        _event_type: 'successful_login',
        _metadata: { mfa_verified: true }
      });
    }
    
    // Clear MFA state
    setMfaRequired(false);
    setPendingMFAUserId(null);
    return { success: true };
  };

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/`;
    
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    return { error };
  };

  const signOut = async () => {
    try {
      // Clear MFA state if active
      if (mfaTimeoutRef.current) {
        clearTimeout(mfaTimeoutRef.current);
      }
      isProcessingMFARef.current = false;
      setMfaRequired(false);
      setPendingMFAUserId(null);
      
      // Sign out from Supabase (this will clear the session)
      const { error } = await supabase.auth.signOut({ scope: 'local' });
      
      if (error) {
        console.error('Error signing out:', error);
      }
      
      // Clear all local state
      setUser(null);
      setSession(null);
      clearAllRoles();
      
      // Force navigation to home
      window.location.href = '/';
    } catch (error) {
      console.error('Error signing out:', error);
      // Force clear everything and redirect even on error
      setUser(null);
      setSession(null);
      clearAllRoles();
      window.location.href = '/';
    }
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      session,
      loading,
      isAdmin, 
      isViewer, 
      isBlogEditor, 
      isBibleSchoolEditor, 
      isAnnouncementEditor, 
      isSermonEditor, 
      isMusicEditor,
      isMusicViewer,
      isMember,
      mfaRequired,
      pendingMFAUserId,
      signIn, 
      signUp, 
      signOut,
      completeMFAVerification
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
